import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import _isEmpty from 'lodash/isEmpty';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';

import PageSectionNavigation from 'components/pageSectionNavigation';
import { LoadingSpinner } from 'components/svg';
import { useCtxHistory } from 'hooks';
import { openSignUpModal } from 'store/modals/actions';
import {
  computeString,
  formatCurrency,
  getStateName,
  importStatic,
  yearlySalaryToHourlyRate,
} from 'utils';
import { CLUSTER_HEADER_CATEGORY, MEDIAN_SALARY_THRESHOLD } from 'utils/constant';

import SalaryChart from '../../components/salaryChart';
import SalaryPercentileTable from '../../components/salaryPercentileTable';
import CommonService from '../../services/commonService';

import Style from './HeaderSection.module.css';

const SalaryChartTooltip = dynamic(() => import('components/salaryChartTooltip'));
const FilterCarousel = dynamic(() => import('components/zpFilter/FilterCarousel'), { ssr: false });

const parseSalary = (salary) => parseFloat(salary.toString().replace(/[^0-9]/g, ''));

const HeaderSection = (props) => {
  const {
    clusterCategory,
    headerGraphicSalaries,
    articleData = [],
    displayFlag,
    lastUpdatedDate,
    ARTICLE_POST_SUBTYPE,
    filters,
    filterGroup,
    jobTitle,
    sidebarComponent,
    salaryCompanyBlockWithCards,
    location,
    showCallout = false,
    city,
    stateAbbr,
    jobTitlePlural,
    jobTitleArticle,
    nationalAverageAnnualSalary,
    nationalAverageHourlySalary,
    companyID,
    companyName,
    renderBlurb = true,
    salaryPercentiles,
    isCombo = false,
    blurbTitle,
    desktopDevice = false,
    SalaryTitleTag,
    sections = [],
  } = props;
  const history = useCtxHistory();
  const dispatch = useDispatch();

  const chartAttributes = headerGraphicSalaries;

  const filtersHistory = useSelector(({ searchFilter }) =>
    searchFilter?.stateHistory?.[filterGroup]?.at(-1)
  );

  const medianSalaryForContent = chartAttributes?.median || chartAttributes?.percentiles[1] || 0;
  const hourlyRateForContent =
    nationalAverageHourlySalary || yearlySalaryToHourlyRate(parseSalary(medianSalaryForContent));

  const [medianSalary, setMedianSalary] = useState(medianSalaryForContent);
  const [hourlyRate, setHourlyRate] = useState(hourlyRateForContent);
  const [entryLevelSalary, setEntryLevelSalary] = useState(chartAttributes?.percentiles?.[0] || 0);
  const [isChartLoading, setIsChartLoading] = useState(false);

  const [newChartAttributes, setNewChartAttributes] = useState(chartAttributes);

  const [currentFilters, setCurrentFilters] = useState(filters);

  const isUserLoggedIn = useSelector(({ user }) => user && user.isLoggedIn);
  const articleHeaderData = articleData.find((item) => item.post_subtype === ARTICLE_POST_SUBTYPE);

  const articleContent = articleHeaderData ? articleHeaderData.content : null;
  const overviewSectionContentBlurb =
    articleData.find((item) => item.post_subtype === 'overview-section')?.content || null;
  const overviewBulletPointContentBlurb =
    articleData.find((item) => item.post_subtype === 'overview-bullet-point')?.content || null;

  const resetData = () => {
    const defaultMedian = chartAttributes?.median || chartAttributes?.percentiles[1] || 0;

    setNewChartAttributes(chartAttributes);
    setMedianSalary(defaultMedian);
    setEntryLevelSalary(chartAttributes?.percentiles?.[0] || 0);
    setHourlyRate(
      nationalAverageHourlySalary || yearlySalaryToHourlyRate(parseSalary(defaultMedian))
    );
  };

  const handleFilterApply = async (filterValues = {}) => {
    let newFilterValues = {};
    Object.keys(filterValues).forEach((key) => {
      const value = filterValues[key][0];
      switch (key) {
        case 'locations':
          newFilterValues = { ...newFilterValues, ...value };
          break;
        case 'degrees':
          newFilterValues = { ...newFilterValues, degree: filterValues[key] };
          break;
        default:
          newFilterValues = { ...newFilterValues, [key]: filterValues[key] };
      }
    });

    if (!Object.keys(newFilterValues).length) {
      resetData();
      return;
    }

    try {
      switch (clusterCategory) {
        case CLUSTER_HEADER_CATEGORY.COMPANY_SALARY: {
          setIsChartLoading(true);
          if (filterValues.department) {
            setCurrentFilters(
              currentFilters.map((curFilter) => {
                const newFilter = { ...curFilter };
                if (newFilter.param === 'jobTitle') {
                  newFilter.disabled = true;
                  newFilter.hiddenOnDisabled = true;
                }
                return newFilter;
              })
            );
          } else if (filterValues.title) {
            setCurrentFilters(
              currentFilters.map((curFilter) => {
                const newFilter = { ...curFilter };
                if (newFilter.param === 'department') {
                  newFilter.disabled = true;
                  newFilter.hiddenOnDisabled = true;
                }
                return newFilter;
              })
            );
          } else {
            setCurrentFilters(filters);
          }

          const companyPayload = {
            ...newFilterValues,
            companyID,
          };

          const companyResponse = await CommonService.fetchCompanyDemographicAdjustmentData(
            companyPayload
          );

          const { median: companyMedian, chart_attributes } = companyResponse;
          const { percentiles: companyPercentiles, ...companyRest } = chart_attributes;

          const newCompanyMedianSalary = _isEmpty(filterValues)
            ? chartAttributes.percentiles[1]
            : formatCurrency(companyMedian);

          const newCompanyPercentiles = companyPercentiles.map((salary) => formatCurrency(salary));
          newCompanyPercentiles[1] = newCompanyMedianSalary;

          setNewChartAttributes({
            colors: companyRest.colors,
            closestToMedian: companyRest.closestToMedian,
            horizontalPos: companyRest.horizontal_pos,
            verticalPos: companyRest.vertical_pos,
            percentiles: newCompanyPercentiles,
          });
          setMedianSalary(newCompanyMedianSalary);
          setHourlyRate(
            (parseFloat(newCompanyMedianSalary.toString().replace(/[^0-9]/g, '')) / 2080).toFixed(2)
          );
          setIsChartLoading(false);
          break;
        }
        default: {
          const payload = {
            ...newFilterValues,
            title: jobTitle,
          };

          const response = await CommonService.fetchDemographicAdjustmentData(payload);
          const { new_median, ...rest1 } = response;
          const { vertical_pos, percentiles, horizontal_pos, ...rest2 } = rest1.chart_attributes;

          const newMedianSalary = _isEmpty(filterValues)
            ? chartAttributes.percentiles[1]
            : formatCurrency(new_median);

          const newEntrySalary = _isEmpty(filterValues)
            ? chartAttributes.percentiles[0]
            : formatCurrency(percentiles[0]);

          const newPercentiles = percentiles.map((salary) => formatCurrency(salary));
          const horizontalPos = horizontal_pos.map((salary) => formatCurrency(salary));
          newPercentiles[1] = newMedianSalary;

          setNewChartAttributes({
            ...rest2,
            verticalPos: vertical_pos,
            percentiles: newPercentiles,
            horizontalPos,
          });

          setMedianSalary(newMedianSalary);
          setEntryLevelSalary(newEntrySalary);
          setHourlyRate(
            (parseFloat(newMedianSalary.toString().replace(/[^0-9]/g, '')) / 2080).toFixed(2)
          );
          break;
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const formatContentBlurb = (content = '') => {
    if (!content || typeof content !== 'string') {
      return content || '';
    }
    if (content.endsWith('<br>')) {
      return content.substring(0, content.lastIndexOf('<br>'));
    }
    return content;
  };

  const showBlurb = jobTitle && stateAbbr && nationalAverageAnnualSalary && jobTitlePlural;
  const jobTitleLowerCase = jobTitle?.toLowerCase?.();
  const jobTitlePluralLower = jobTitlePlural?.toLowerCase?.();
  const jobTitleInSentence =
    jobTitle && jobTitle[0].toUpperCase() + jobTitle.slice(1).toLowerCase();
  const isLowIncome = parseSalary(medianSalary) < MEDIAN_SALARY_THRESHOLD;

  const generateBlurb = () => {
    if (isCombo) {
      const cityOrState = city ? city : getStateName(stateAbbr);
      if (isLowIncome) {
        const lowestSalary = yearlySalaryToHourlyRate(
          parseSalary(chartAttributes?.percentiles?.[0] || 0)
        );
        const highestSalary = yearlySalaryToHourlyRate(
          parseSalary(chartAttributes?.percentiles?.[2] || 0)
        );
        return (
          <>
            <p>
              {`The average hourly rate for ${jobTitleArticle} ${jobTitleLowerCase} in ${location}
              is `}
              <b>${hourlyRateForContent}</b>
              {` per hour. `}

              {highestSalary &&
                `${jobTitleInSentence} hourly rates in ${cityOrState} typically range between $${lowestSalary} and $${highestSalary} an hour. `}

              {`This compares to a national average ${jobTitleLowerCase} hourly rate of $${hourlyRateForContent}. `}

              {`The average salary for ${jobTitleArticle} ${jobTitleLowerCase} in ${location} is `}

              {<b>{medianSalaryForContent}</b>}
            </p>
            <p className="z-mb-0">
              {`
              Below, we break down the average ${jobTitleLowerCase} salary in ${location} by the highest paying companies and industries.
              You can also compare different types of ${jobTitleLowerCase} salaries in and around ${cityOrState} and a salary history chart that shows how the average salary for ${jobTitleLowerCase} has changed over time in ${cityOrState}.`}
            </p>
          </>
        );
      } else {
        const lowestSalary = chartAttributes?.percentiles?.[0] || 0;
        const highestSalary = chartAttributes?.percentiles?.[2] || 0;
        if (city) {
          return (
            <>
              <p>
                {`The average ${jobTitleLowerCase} salary in ${location} is ${medianSalaryForContent}. ${jobTitleInSentence} salaries in ${city} range between ${lowestSalary} and ${highestSalary} a year. This ${city} based salary compares to a United States average ${jobTitleLowerCase} salary of ${medianSalaryForContent}. The average hourly rate for ${jobTitlePluralLower} in ${city} is $${hourlyRateForContent} per hour.`}
              </p>
              <p className="z-mb-0">
                {`The average ${jobTitleLowerCase} salary in ${city} is further broken down below by the highest-paying companies and industries. You can also compare the salaries of different types of ${jobTitlePluralLower} in ${city} and see how the average ${jobTitleLowerCase} salaries have changed over time in ${city}.`}
              </p>
            </>
          );
        } else {
          const stateName = getStateName(stateAbbr);
          return (
            <>
              <p>
                {`The average ${jobTitleLowerCase} salary in ${stateName} is ${medianSalaryForContent}. ${jobTitleInSentence} salaries in ${stateName} typically range between ${lowestSalary} and ${highestSalary} a year. This compares to a United States average ${jobTitleLowerCase} salary of ${medianSalaryForContent}. The average hourly rate for ${jobTitlePluralLower} in ${stateName} is $${hourlyRateForContent} per hour.`}
              </p>
              <p className="z-mb-0">
                {`The average ${jobTitleLowerCase} salary in ${stateName} is further broken down below by the highest-paying companies and industries. You can also compare salaries of different types of ${jobTitlePluralLower} in ${stateName} and see how the average salary for ${jobTitlePluralLower} has changed over time in ${stateName}.`}
              </p>
            </>
          );
        }
      }
    }
    return (
      <p className="z-mb-0">
        {`The average ${jobTitleLowerCase} salary in ${city}, ${stateAbbr} is ${medianSalaryForContent} annually. The average hourly rate for a ${jobTitleLowerCase} is $${hourlyRateForContent}/hr.
                This compares to the national average ${jobTitleLowerCase} salary of ${formatCurrency(
          nationalAverageAnnualSalary
        )}. Below, we break down the average ${jobTitleLowerCase} salary in ${city}, ${stateAbbr} by the highest paying companies and industries.
                You can also compare different types of ${jobTitleLowerCase} salaries in and around ${city} and a salary history chart that shows how the average salary for ${jobTitlePluralLower} has changed over time in ${city}.`}
      </p>
    );
  };

  const renderContentBlurb = () => {
    let content;
    if (showBlurb) {
      content = generateBlurb();
    } else if (articleContent) {
      content = <div dangerouslySetInnerHTML={{ __html: formatContentBlurb(articleContent) }} />;
    }

    if (!(displayFlag && content)) return null;

    if (isCombo) {
      return (
        <div className="content-blurb z-mt-48">
          <h3 className="zp-title-h3 z-mb-16">
            {computeString(
              'How much does',
              jobTitleArticle,
              jobTitle.toLowerCase(),
              'make',
              !!location && `in ${location}?`
            )}
          </h3>
          {content}
        </div>
      );
    }

    return (
      <>
        {displayFlag && content && (
          <div className="how-much-make content-blurb mt-4">
            <div className="openSansBlackBold mb-2">
              {blurbTitle
                ? blurbTitle
                : location
                ? `What Is The Average ${jobTitle} Salary In ${location}?`
                : `What Is The Average ${jobTitle} Salary?`}
            </div>
            {content}
          </div>
        )}
      </>
    );
  };

  const onCalloutClick = () => {
    const query = {
      survey_step: 'step1',
      survey: '201',
      src: 'career-salary-page',
    };

    if (isUserLoggedIn) {
      query.skipQuestion = [7];
    }

    dispatch(
      openSignUpModal(
        {
          ...query,
        },
        true
      )
    )
      .then(() => {
        history.pushQuery(query);
      })
      .catch((err) => console.error(err));
  };

  const showBlurbForCombo = isCombo ? desktopDevice : true;

  const renderH2Title = () => {
    let h2Title = '';

    const jobTitleTrimmed = jobTitle ? jobTitle.toLowerCase().trim() : '';

    let locationTrimmed = location ? location.trim() : '';
    const filterLocation = filtersHistory?.location?.[0];

    if (filterLocation?.displayName) {
      locationTrimmed = filterLocation.displayName;
    }

    const companyNameTrimmed = companyName ? companyName.trim() : '';

    // Job Title + Location combo page
    if (locationTrimmed && jobTitleTrimmed) {
      h2Title = `Average ${jobTitleTrimmed} salary in ${locationTrimmed}`;
    }

    // Job Title + Company combo page
    else if (jobTitleTrimmed && companyNameTrimmed) {
      h2Title = `Average ${companyNameTrimmed} ${jobTitleTrimmed?.toLowerCase?.()} salary`;
    }

    // Location + Company combo page;
    else if (locationTrimmed && companyNameTrimmed) {
      h2Title = `Average ${companyNameTrimmed} salary in ${locationTrimmed}`;
    }

    // Company Salary Page
    else if (companyNameTrimmed && !jobTitleTrimmed && !locationTrimmed) {
      h2Title = `${companyNameTrimmed} salary estimate`;
    }

    // Job Title Salary Page
    else if (jobTitleTrimmed && !companyNameTrimmed && !locationTrimmed) {
      h2Title = `Average ${jobTitleTrimmed} salary`;
    }

    // This else condition is very unlikely
    else {
      h2Title = 'Average salary estimate';
    }

    return h2Title;
  };

  const renderEntryLevelText = () => {
    if (companyName && city) {
      return (
        <>
          <div className="text-capitalize">
            {computeString('Entry-level', companyName, 'salary in', city)}
          </div>
          <span>{`${entryLevelSalary} yearly`}</span>
        </>
      );
    }

    if (city || stateAbbr) {
      return (
        <>
          <div className="text-capitalize">{`Entry-level${
            city ? '' : ` ${getStateName(stateAbbr)}`
          }${jobTitle ? ` ${jobTitle.toLowerCase?.()}` : ''} salary`}</div>
          <span>{`${entryLevelSalary} yearly`}</span>
        </>
      );
    }

    if (companyName && jobTitle) {
      return (
        <>
          <div className="text-capitalize">
            {`Entry-level ${companyName} ${jobTitle?.toLowerCase?.()} salary`}
          </div>
          <span>{`${entryLevelSalary} yearly`}</span>
        </>
      );
    }

    return (
      <>
        <div className="text-capitalize">{`Entry-level${
          jobTitle ? ` ${jobTitle.toLowerCase?.()}` : ''
        } salary`}</div>
        <span>{entryLevelSalary} yearly salary</span>
      </>
    );
  };

  return (
    <div className="d-flex flex-column flex-xl-row justify-content-between header-section-container">
      <div className="w-730 flex-fill">
        {chartAttributes && (
          <>
            <div
              className={`salary-designer-body z-p-20 z-p-20 ${
                !showCallout ? 'no-salary-call-out' : ''
              }`}
            >
              <div className="salary-graphic-area-left w-100">
                <div className="salary-job-filters-container d-md-flex justify-content-md-between align-items-md-center">
                  <FilterCarousel
                    filters={currentFilters}
                    filterGroup={filterGroup}
                    onApply={handleFilterApply}
                    containerClass="p-0"
                    filtersCategory="salary"
                  />
                  <div>
                    {lastUpdatedDate && (
                      <div
                        itemProp="dateModified"
                        className="last-updated-date"
                      >{`Updated ${lastUpdatedDate}`}</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="salary-graphic-area-right z-mt-md-36 z-mt-10">
                <div className="median-salary-container">
                  <div>
                    <h2
                      className={classNames(
                        Style.salaryGraphJobTitleHeader,
                        `openSansBlackBold mb-2 text-capitalize`
                      )}
                    >
                      {renderH2Title()}
                    </h2>
                    <span className="d-flex align-items-end z-mb-5">
                      <SalaryTitleTag className="average-salary-number m-0 d-flex align-items-end">
                        {isLowIncome ? `$${hourlyRate}` : medianSalary}
                        <span className="z-ml-4">{isLowIncome ? 'hourly' : ''}</span>
                      </SalaryTitleTag>
                      <SalaryChartTooltip />
                    </span>
                    <div className="hourly-rate-number">
                      {city || stateAbbr ? (
                        `${isLowIncome ? medianSalary : `$${hourlyRate}`} ${
                          isLowIncome ? ' yearly' : ' hourly'
                        }`
                      ) : (
                        <>
                          {isLowIncome ? medianSalary : `$${hourlyRate}`}
                          <span>{isLowIncome ? ' yearly rate' : ' hourly rate'}</span>
                        </>
                      )}
                    </div>
                    <hr className="z-my-16" />
                    <div className="entry-level-salary">{renderEntryLevelText()}</div>
                  </div>
                  <div className="salary-chart-wrapper">
                    {isChartLoading ? (
                      <LoadingSpinner />
                    ) : (
                      <SalaryChart showTooltip chartAttributes={newChartAttributes} />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {showCallout && (
              <div className={`${Style.calloutWrap} openSansBlackBold mt-24`}>
                <div class={Style.calloutImageContainer}>
                  <img
                    width={38}
                    height={39}
                    src={importStatic('/ui-router/images/callouts/salary/calculator.svg')}
                    alt="Calculator"
                  />
                </div>
                <div className={Style.calloutText}>
                  {'Are you paid fairly? '}
                  <span className="list-link" onClick={onCalloutClick}>
                    Get Your Salary Estimate
                  </span>
                </div>
              </div>
            )}
          </>
        )}

        {renderBlurb && showBlurbForCombo && renderContentBlurb()}

        {!_isEmpty(overviewBulletPointContentBlurb) && !_isEmpty(overviewSectionContentBlurb) && (
          <div>
            <div
              className="z-mt-20 z-mb-16 content-blurb"
              dangerouslySetInnerHTML={{
                __html: formatContentBlurb(overviewSectionContentBlurb),
              }}
            />
            <div
              className="content-blurb"
              dangerouslySetInnerHTML={{
                __html: formatContentBlurb(overviewBulletPointContentBlurb),
              }}
            />
          </div>
        )}

        {!!sections.length && (
          <>
            <div className="brandonH3 my-3">On This Page</div>
            <div className="d-flex flex-column flex-md-row align-items-md-center">
              <span className="mb-1 mb-md-0">{`${jobTitleInSentence} salary by:`}&nbsp;</span>
              <PageSectionNavigation sections={sections} />
            </div>
          </>
        )}

        {!!salaryPercentiles?.length && (
          <SalaryPercentileTable
            title={`What is ${jobTitleArticle} ${jobTitle}'s Salary?`}
            customClass={classNames(Style.salaryPercentileTable, 'mt-5')}
            percentiles={salaryPercentiles}
          />
        )}
        {salaryCompanyBlockWithCards}
      </div>
      <div className="d-block d-xl-none z-mt-16">
        {isCombo ? sidebarComponent(renderContentBlurb) : sidebarComponent}
      </div>
    </div>
  );
};

HeaderSection.propTypes = {
  headerGraphicSalaries: PropTypes.object.isRequired,
  articleData: PropTypes.arrayOf(PropTypes.object).isRequired,
  salaryPercentiles: PropTypes.arrayOf(PropTypes.object),
  displayFlag: PropTypes.bool.isRequired,
  jobTitle: PropTypes.string.isRequired,
  title: PropTypes.string,
  sidebarComponent: PropTypes.any,
  salaryCompanyBlockWithCards: PropTypes.any,
  location: PropTypes.string,
  nationalAverageHourlySalary: PropTypes.string,
  nationalAverageAnnualSalary: PropTypes.string,
  clusterCategory: PropTypes.string,
  companyID: PropTypes.number,
  companyName: PropTypes.string,
  jobTitlePlural: PropTypes.string,
  ARTICLE_POST_SUBTYPE: PropTypes.string,
  lastUpdatedDate: PropTypes.string,
  filters: PropTypes.any,
  filterGroup: PropTypes.string,
  showCallout: PropTypes.bool,
  blurbTitle: PropTypes.any,
  SalaryTitleTag: PropTypes.string,
  desktopDevice: PropTypes.bool,
  jobTitleArticle: PropTypes.string,
  renderBlurb: PropTypes.bool,
  stateAbbr: PropTypes.string,
  city: PropTypes.string,
};

HeaderSection.defaultProps = {
  subtitle: 'Average Yearly Salary',
  salaryPercentiles: [],
  title: undefined,
  location: undefined,
  nationalAverageHourlySalary: undefined,
  clusterCategory: undefined,
  companyID: undefined,
  companyName: undefined,
  SalaryTitleTag: 'h3',
  salaryCompanyBlockWithCards: null,
};

export default HeaderSection;
